// AdminBookingsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  orderBy,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { normalizeBookingStatus } from '../utils/statusNormalizer';
import './AdminStyles.css';

// Booking Status Constants
const BOOKING_STATUS = {
  AVAILABLE: 'available',      // White - Available for selection
  PAST: 'past',                // Black - In the past
  NOT_SUBMITTED: 'not_submitted', // Blue - Not yet submitted
  PENDING: 'pending',          // Yellow - Pending confirmation
  CONFIRMED: 'confirmed',      // Green - Confirmed
  NEEDS_ATTENTION: 'needs_attention' // Red - Rejected/needs attention
};

// Status display configuration
const STATUS_CONFIG = {
  [BOOKING_STATUS.AVAILABLE]: { 
    label: 'Available', 
    color: '#ffffff', 
    bgColor: '#6c757d',
    textColor: '#000000'
  },
  [BOOKING_STATUS.PAST]: { 
    label: 'Past', 
    color: '#000000', 
    bgColor: '#343a40',
    textColor: '#ffffff'
  },
  [BOOKING_STATUS.NOT_SUBMITTED]: { 
    label: 'Not Submitted', 
    color: '#0066cc', 
    bgColor: '#cfe2ff',
    textColor: '#084298'
  },
  [BOOKING_STATUS.PENDING]: { 
    label: 'Pending', 
    color: '#ffc107', 
    bgColor: '#fff3cd',
    textColor: '#664d03'
  },
  [BOOKING_STATUS.CONFIRMED]: { 
    label: 'Confirmed', 
    color: '#28a745', 
    bgColor: '#d1e7dd',
    textColor: '#0f5132'
  },
  [BOOKING_STATUS.NEEDS_ATTENTION]: { 
    label: 'Needs Attention', 
    color: '#dc3545', 
    bgColor: '#f8d7da',
    textColor: '#842029'
  }
};

export default function AdminBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState({});
  const [journeys, setJourneys] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [financialData, setFinancialData] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Sorting and filtering states for active bookings
  const [sortBy, setSortBy] = useState('submittedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterByUser, setFilterByUser] = useState('');
  const [filterByJourney, setFilterByJourney] = useState('');
  const [filterByStatus, setFilterByStatus] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  
  // Sorting and filtering states for past bookings
  const [pastSortBy, setPastSortBy] = useState('bookingDate');
  const [pastSortOrder, setPastSortOrder] = useState('desc');
  const [pastFilterByUser, setPastFilterByUser] = useState('');
  const [pastFilterByJourney, setPastFilterByJourney] = useState('');
  const [pastDateFilter, setPastDateFilter] = useState({ start: '', end: '' });

  // Load bookings and related data
  useEffect(() => {
    loadAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Auto-update past bookings status - only run once after initial load
  useEffect(() => {
    let mounted = true;
    const runAutoUpdate = async () => {
      // Wait a bit for initial load to complete
      await new Promise(resolve => setTimeout(resolve, 2000));
      if (mounted) {
        await checkAndUpdatePastBookings();
      }
    };
    
    runAutoUpdate();
    
    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load all journeys with hostel assignments
      const journeysSnapshot = await getDocs(collection(db, 'journeys'));
      const journeysData = {};
      const allBookings = [];
      
      for (const journeyDoc of journeysSnapshot.docs) {
        const journeyData = { id: journeyDoc.id, ...journeyDoc.data() };
        journeysData[journeyDoc.id] = journeyData;
        
        // Extract hostel assignments as bookings
        if (journeyData.hostelAssignments && Array.isArray(journeyData.hostelAssignments)) {
          journeyData.hostelAssignments.forEach((assignment, dayIndex) => {
            if (assignment && assignment.id) { // Only process if there's actually a hostel selected
              // Determine status based on date
              const bookingDate = calculateBookingDate(journeyData.startDate, dayIndex);
              const isPast = bookingDate && new Date(bookingDate) < new Date();
              
              // Determine correct status - handle both lowercase and capitalized versions
              let originalStatus = assignment.status;
              let finalStatus = null;
              
              // Handle different status formats from mobile app
              if (originalStatus) {
                // Normalize to lowercase and trim whitespace
                const normalizedStatus = originalStatus.toString().toLowerCase().trim();
                
                if (normalizedStatus === 'pending') {
                  // Check if booking was actually submitted
                  // If bookingSubmittedAt exists, it means the user pressed "Go"
                  if (journeyData.bookingSubmittedAt) {
                    finalStatus = BOOKING_STATUS.PENDING;
                  } else {
                    // Status says "Pending" but no submission timestamp - treat as not submitted
                    finalStatus = BOOKING_STATUS.NOT_SUBMITTED;
                  }
                } else if (normalizeBookingStatus(normalizedStatus) === 'not_submitted') {
                  // "Not Submitted" means selected but not yet submitted via "Go"
                  finalStatus = BOOKING_STATUS.NOT_SUBMITTED;
                } else if (normalizeBookingStatus(normalizedStatus) === 'confirmed' || normalizeBookingStatus(normalizedStatus) === 'booked') {
                  finalStatus = BOOKING_STATUS.CONFIRMED;
                } else if (normalizeBookingStatus(normalizedStatus) === 'needs_attention' || normalizedStatus === 'rejected') {
                  finalStatus = BOOKING_STATUS.NEEDS_ATTENTION;
                } else {
                  // Use the normalized status as-is
                  finalStatus = normalizedStatus;
                }
              }
              
              if (!finalStatus) {
                // No status set yet - determine based on context
                if (isPast) {
                  finalStatus = BOOKING_STATUS.PAST;
                } else if (journeyData.bookingSubmittedAt) {
                  // Has a submission timestamp but no status - treat as pending
                  finalStatus = BOOKING_STATUS.PENDING;
                } else {
                  // Hostel is selected but no status or submission - default to NOT_SUBMITTED
                  finalStatus = BOOKING_STATUS.NOT_SUBMITTED;
                }
              } else if (isPast && (finalStatus === BOOKING_STATUS.NOT_SUBMITTED || finalStatus === BOOKING_STATUS.PENDING)) {
                // Override to past if the date has passed and it wasn't confirmed
                finalStatus = BOOKING_STATUS.PAST;
              }
              
              // Create unique ID that includes more specificity
              allBookings.push({
                ...assignment, // Spread first to get all assignment fields
                id: `${journeyDoc.id}_day_${dayIndex}_${assignment.id || Math.random()}`,
                journeyId: journeyDoc.id,
                journeyName: journeyData.name || journeyData.routeName,
                userId: journeyData.userId,
                dayIndex: dayIndex,
                bookingDate: bookingDate,
                hostelName: assignment.name || 'Unnamed Hostel',
                hostelId: assignment.id,
                cityName: assignment.cityName || '',
                status: finalStatus, // Always use the processed/normalized status
                submittedAt: journeyData.bookingSubmittedAt || journeyData.createdAt,
                lastModified: assignment.lastModified || journeyData.updatedAt,
                price: assignment.price || assignment.pricePerBed || 0,
                currency: assignment.currency || journeyData.currency || 'USD',
                actualPrice: assignment.actualPrice || null,
                priceDelta: assignment.priceDelta || null,
                bookingInstructions: assignment.bookingInstructions || '',
                needsAttentionReason: assignment.needsAttentionReason || '',
                backendNotes: assignment.backendNotes || '',
                hostelNotes: assignment.notes || '' // Keep the hostel's notes field separate
              });
            }
          });
        }
      }
      
      setJourneys(journeysData);
      
      // Load user data
      const userIds = [...new Set(allBookings.map(b => b.userId).filter(Boolean))];
      const usersData = {};
      
      for (const userId of userIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            usersData[userId] = userDoc.data();
          }
        } catch (error) {
          console.error(`Error loading user ${userId}:`, error);
        }
      }
      
      setUsers(usersData);
      
      // Load financial data from a separate collection if it exists
      try {
        const financialSnapshot = await getDocs(collection(db, 'bookingFinancials'));
        const financialMap = {};
        financialSnapshot.forEach(doc => {
          financialMap[doc.id] = doc.data();
        });
        setFinancialData(financialMap);
      } catch (error) {
        console.error('Error loading financial data:', error);
      }
      
      // Load audit logs
      try {
        const auditSnapshot = await getDocs(
          query(collection(db, 'bookingAuditLogs'), orderBy('timestamp', 'desc'))
        );
        const logs = auditSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAuditLogs(logs);
      } catch (error) {
        console.error('Error loading audit logs:', error);
      }
      
      setBookings(allBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
      alert('Error loading bookings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate booking date based on journey start date and day index
  const calculateBookingDate = (startDate, dayIndex) => {
    if (!startDate) return null;
    const date = new Date(startDate);
    date.setDate(date.getDate() + dayIndex);
    return date.toISOString().split('T')[0];
  };
  
  // Check and update past bookings status (only runs once)
  const checkAndUpdatePastBookings = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    try {
      // Get all journeys to check for past bookings
      const journeysSnapshot = await getDocs(collection(db, 'journeys'));
      let hasUpdates = false;
      
      for (const journeyDoc of journeysSnapshot.docs) {
        const journeyData = journeyDoc.data();
        
        if (journeyData.hostelAssignments && Array.isArray(journeyData.hostelAssignments)) {
          const updatedAssignments = [...journeyData.hostelAssignments];
          let needsUpdate = false;
          
          // eslint-disable-next-line no-loop-func
          journeyData.hostelAssignments.forEach((assignment, dayIndex) => {
            if (assignment) {
              const bookingDate = calculateBookingDate(journeyData.startDate, dayIndex);
              if (bookingDate) {
                const bookingDateObj = new Date(bookingDate);
                bookingDateObj.setHours(0, 0, 0, 0);
                
                // If booking is in the past and not already marked as past or confirmed
                if (bookingDateObj < today && 
                    assignment.id && // Only if hostel is selected
                    assignment.status !== BOOKING_STATUS.PAST &&
                    assignment.status !== BOOKING_STATUS.CONFIRMED &&
                    assignment.status !== BOOKING_STATUS.NEEDS_ATTENTION) {
                  updatedAssignments[dayIndex] = {
                    ...assignment,
                    status: BOOKING_STATUS.PAST,
                    lastModified: new Date().toISOString(),
                    lastModifiedBy: 'System Auto-Update'
                  };
                  needsUpdate = true;
                  hasUpdates = true;
                }
              }
            }
          });
          
          if (needsUpdate) {
            await updateDoc(doc(db, 'journeys', journeyDoc.id), {
              hostelAssignments: updatedAssignments,
              lastBookingUpdate: new Date().toISOString()
            });
            console.log(`Auto-updated past bookings for journey ${journeyDoc.id}`);
          }
        }
      }
      
      // Only reload if we made updates
      if (hasUpdates) {
        await loadAllData();
      }
    } catch (error) {
      console.error('Error checking past bookings:', error);
    }
  };

  // Delete/Clear a booking
  const deleteBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to DELETE this booking?\n\nHostel: ${booking.hostelName}\nDate: ${booking.bookingDate}\nUser: ${users[booking.userId]?.firstName} ${users[booking.userId]?.lastName}\n\nThis will clear the hostel selection for Day ${booking.dayIndex + 1} and allow the traveler to select again.`)) {
      return;
    }
    
    try {
      const auth = getAuth();
      const adminUser = auth.currentUser;
      
      // Get the journey document
      const journeyRef = doc(db, 'journeys', booking.journeyId);
      const journeyDoc = await getDoc(journeyRef);
      const journeyData = journeyDoc.data();
      
      // Clear the hostel assignment for this day
      const updatedAssignments = [...journeyData.hostelAssignments];
      updatedAssignments[booking.dayIndex] = null; // Clear the booking
      
      await updateDoc(journeyRef, {
        hostelAssignments: updatedAssignments,
        lastBookingUpdate: new Date().toISOString()
      });
      
      // Create audit log entry for the deletion
      await addDoc(collection(db, 'bookingAuditLogs'), {
        bookingId: booking.id,
        journeyId: booking.journeyId,
        userId: booking.userId,
        dayIndex: booking.dayIndex,
        previousStatus: booking.status,
        newStatus: 'DELETED',
        changedBy: adminUser?.email,
        timestamp: serverTimestamp(),
        action: 'DELETE_BOOKING',
        bookingDetails: {
          hostelName: booking.hostelName,
          bookingDate: booking.bookingDate,
          price: booking.price
        }
      });
      
      // Reload data
      await loadAllData();
      
      alert(`Booking deleted successfully. The traveler can now select a new hostel for Day ${booking.dayIndex + 1}.`);
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Error deleting booking: ' + error.message);
    }
  };

  // Update booking status
  const updateBookingStatus = async (booking, newStatus, additionalData = {}) => {
    try {
      const auth = getAuth();
      const adminUser = auth.currentUser;
      
      // Update the journey document
      const journeyRef = doc(db, 'journeys', booking.journeyId);
      const journeyDoc = await getDoc(journeyRef);
      const journeyData = journeyDoc.data();
      
      const updatedAssignments = [...journeyData.hostelAssignments];
      updatedAssignments[booking.dayIndex] = {
        ...updatedAssignments[booking.dayIndex],
        status: newStatus,
        lastModified: new Date().toISOString(),
        lastModifiedBy: adminUser?.email,
        ...additionalData
      };
      
      await updateDoc(journeyRef, {
        hostelAssignments: updatedAssignments,
        lastBookingUpdate: new Date().toISOString()
      });
      
      // Create audit log entry
      await addDoc(collection(db, 'bookingAuditLogs'), {
        bookingId: booking.id,
        journeyId: booking.journeyId,
        userId: booking.userId,
        dayIndex: booking.dayIndex,
        previousStatus: booking.status,
        newStatus: newStatus,
        changedBy: adminUser?.email,
        timestamp: serverTimestamp(), // This is okay - not in an array
        additionalData: additionalData,
        bookingDetails: {
          hostelName: booking.hostelName,
          bookingDate: booking.bookingDate,
          price: booking.price
        }
      });
      
      // Update financial records if cost data is provided
      if (additionalData.backpassCost !== undefined) {
        await updateDoc(doc(db, 'bookingFinancials', booking.id), {
          backpassCost: additionalData.backpassCost,
          lastUpdated: serverTimestamp(),
          updatedBy: adminUser?.email
        });
      }
      
      // Send email notification for certain status changes
      if (newStatus === BOOKING_STATUS.CONFIRMED || newStatus === BOOKING_STATUS.NEEDS_ATTENTION) {
        await sendStatusChangeNotification(booking, newStatus, additionalData);
      }
      
      // Reload data
      await loadAllData();
      
      alert(`Booking status updated to ${STATUS_CONFIG[newStatus].label}`);
      setShowStatusModal(false);
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Error updating booking status: ' + error.message);
    }
  };

  // Send email notification
  const sendStatusChangeNotification = async (booking, newStatus, additionalData) => {
    try {
      const user = users[booking.userId];
      if (!user?.email) {
        console.error('No user email found for booking notification');
        return;
      }
      
      // Only send email for confirmed or needs attention statuses
      if (newStatus !== BOOKING_STATUS.CONFIRMED && newStatus !== BOOKING_STATUS.NEEDS_ATTENTION) {
        return;
      }
      
      // Prepare booking details for notification
      const bookingDetails = {
        hostelName: booking.hostelName,
        bookingDate: booking.bookingDate,
        cityName: booking.cityName,
        journeyName: booking.journeyName,
        dayNumber: booking.dayIndex + 1,
        status: newStatus,
        statusLabel: STATUS_CONFIG[newStatus].label,
        // Send appropriate message based on status
        bookingInstructions: newStatus === BOOKING_STATUS.CONFIRMED ? additionalData.bookingInstructions : undefined,
        needsAttentionReason: newStatus === BOOKING_STATUS.NEEDS_ATTENTION ? additionalData.needsAttentionReason : undefined,
        actualPrice: additionalData.actualPrice,
        currency: booking.currency || 'USD'
      };
      
      // Send email notification to user and admin
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://backpass-backend.onrender.com';
      const response = await fetch(`${backendUrl}/api/send-booking-notification`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email,
          userName: user.firstName || 'Guest',
          adminEmail: 'matthew@backpass.co', // Always CC admin
          bookingDetails: bookingDetails,
          notificationType: newStatus === BOOKING_STATUS.CONFIRMED ? 'booking_confirmed' : 'needs_attention'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to send notification email:', errorData);
        // Don't throw error - notification failure shouldn't block the status update
        console.warn('Booking status updated but email notification failed');
      } else {
        console.log('Booking notification sent successfully');
      }
      
      // Placeholder for push notifications
      // This will be implemented when iOS/Android push notifications are set up
      await sendPushNotification(booking, newStatus, user, bookingDetails);
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  // Placeholder for push notifications - to be implemented when iOS/Android notifications are set up
  const sendPushNotification = async (booking, newStatus, user, bookingDetails) => {
    try {
      // TODO: Implement push notifications when iOS/Android setup is complete
      // This will integrate with Firebase Cloud Messaging (FCM) or similar service
      
      console.log('Push notification placeholder:', {
        userId: booking.userId,
        deviceTokens: user.deviceTokens || [], // Will be stored in user profile
        notification: {
          title: newStatus === BOOKING_STATUS.CONFIRMED 
            ? `Booking Confirmed: ${booking.hostelName}` 
            : `Action Required: ${booking.hostelName}`,
          body: newStatus === BOOKING_STATUS.CONFIRMED
            ? `Your booking for ${booking.bookingDate} has been confirmed. Check your email for instructions.`
            : `Your booking for ${booking.bookingDate} needs attention. ${bookingDetails.needsAttentionReason || 'Please check your email for details.'}`,
          data: {
            type: 'booking_status_update',
            bookingId: booking.id,
            journeyId: booking.journeyId,
            status: newStatus,
            deepLink: `/journey/${booking.journeyId}` // For deep linking in mobile app
          }
        }
      });
      
      // When implementing, this would call a backend endpoint or use Firebase Admin SDK
      // Example structure:
      /*
      const response = await fetch(`${backendUrl}/api/send-push-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: booking.userId,
          notification: { ... }
        })
      });
      */
      
    } catch (error) {
      // Silently fail for push notifications as they're not critical
      console.log('Push notification not sent (not yet implemented):', error);
    }
  };

  // Split bookings into active and past
  const { activeBookings, pastBookings } = useMemo(() => {
    const active = [];
    const past = [];
    
    bookings.forEach(booking => {
      if (booking.status === BOOKING_STATUS.PAST) {
        past.push(booking);
      } else {
        active.push(booking);
      }
    });
    
    return { activeBookings: active, pastBookings: past };
  }, [bookings]);
  
  // Filtered and sorted active bookings
  const filteredAndSortedBookings = useMemo(() => {
    let filtered = [...activeBookings];
    
    // Apply filters
    if (filterByUser) {
      filtered = filtered.filter(b => b.userId === filterByUser);
    }
    if (filterByJourney) {
      filtered = filtered.filter(b => b.journeyId === filterByJourney);
    }
    if (filterByStatus) {
      filtered = filtered.filter(b => b.status === filterByStatus);
    }
    if (dateFilter.start) {
      filtered = filtered.filter(b => b.bookingDate >= dateFilter.start);
    }
    if (dateFilter.end) {
      filtered = filtered.filter(b => b.bookingDate <= dateFilter.end);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle date sorting
      if (sortBy === 'submittedAt' || sortBy === 'bookingDate' || sortBy === 'lastModified') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [activeBookings, filterByUser, filterByJourney, filterByStatus, dateFilter, sortBy, sortOrder]);
  
  // Filtered and sorted past bookings
  const filteredAndSortedPastBookings = useMemo(() => {
    let filtered = [...pastBookings];
    
    // Apply filters
    if (pastFilterByUser) {
      filtered = filtered.filter(b => b.userId === pastFilterByUser);
    }
    if (pastFilterByJourney) {
      filtered = filtered.filter(b => b.journeyId === pastFilterByJourney);
    }
    if (pastDateFilter.start) {
      filtered = filtered.filter(b => b.bookingDate >= pastDateFilter.start);
    }
    if (pastDateFilter.end) {
      filtered = filtered.filter(b => b.bookingDate <= pastDateFilter.end);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[pastSortBy];
      let bVal = b[pastSortBy];
      
      // Handle date sorting
      if (pastSortBy === 'submittedAt' || pastSortBy === 'bookingDate' || pastSortBy === 'lastModified') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }
      
      if (aVal < bVal) return pastSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return pastSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [pastBookings, pastFilterByUser, pastFilterByJourney, pastDateFilter, pastSortBy, pastSortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  
  const handlePastSort = (field) => {
    if (pastSortBy === field) {
      setPastSortOrder(pastSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPastSortBy(field);
      setPastSortOrder('desc');
    }
  };

  const StatusChangeModal = () => {
    const [newStatus, setNewStatus] = useState(selectedBooking?.status || '');
    const [actualPrice, setActualPrice] = useState(selectedBooking?.actualPrice ? String(selectedBooking.actualPrice) : '');
    const [bookingInstructions, setBookingInstructions] = useState(selectedBooking?.bookingInstructions || '');
    const [needsAttentionReason, setNeedsAttentionReason] = useState(selectedBooking?.needsAttentionReason || '');
    const [backendNotes, setBackendNotes] = useState(selectedBooking?.backendNotes || '');
    
    // Calculate delta (difference between estimated and actual price)
    const estimatedPrice = selectedBooking?.price || selectedBooking?.pricePerBed || 0;
    const delta = actualPrice ? (parseFloat(actualPrice) - estimatedPrice).toFixed(2) : null;
    
    if (!selectedBooking) return null;
    
    // Only allow certain status transitions
    const canChangeStatus = selectedBooking.status === BOOKING_STATUS.PENDING || 
                           selectedBooking.status === BOOKING_STATUS.NOT_SUBMITTED ||
                           selectedBooking.status === BOOKING_STATUS.CONFIRMED ||
                           selectedBooking.status === BOOKING_STATUS.NEEDS_ATTENTION;
    
    let allowedStatuses = [selectedBooking.status];
    if (selectedBooking.status === BOOKING_STATUS.NOT_SUBMITTED) {
      // From Not Submitted, can mark as Pending (acknowledging receipt)
      allowedStatuses = [BOOKING_STATUS.NOT_SUBMITTED, BOOKING_STATUS.PENDING];
    } else if (selectedBooking.status === BOOKING_STATUS.PENDING) {
      // From Pending, can go to Confirmed or Needs Attention
      allowedStatuses = [BOOKING_STATUS.PENDING, BOOKING_STATUS.CONFIRMED, BOOKING_STATUS.NEEDS_ATTENTION];
    } else if (selectedBooking.status === BOOKING_STATUS.CONFIRMED) {
      // From Confirmed, can change to Needs Attention if there's an issue
      allowedStatuses = [BOOKING_STATUS.CONFIRMED, BOOKING_STATUS.NEEDS_ATTENTION];
    } else if (selectedBooking.status === BOOKING_STATUS.NEEDS_ATTENTION) {
      // From Needs Attention, can go back to Pending or mark as Confirmed when resolved
      allowedStatuses = [BOOKING_STATUS.NEEDS_ATTENTION, BOOKING_STATUS.PENDING, BOOKING_STATUS.CONFIRMED];
    }
    
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{ maxWidth: '600px' }}>
          <h3>Update Booking Status</h3>
          
          <div className="booking-detail-card">
            <h4>{selectedBooking.hostelName}</h4>
            <p>Guest: {users[selectedBooking.userId]?.firstName} {users[selectedBooking.userId]?.lastName}</p>
            <p>Date: {selectedBooking.bookingDate}</p>
            <p>Journey: {selectedBooking.journeyName}</p>
            <p>Current Status: <strong style={{ color: STATUS_CONFIG[selectedBooking.status]?.color }}>
              {STATUS_CONFIG[selectedBooking.status]?.label}
            </strong></p>
            {selectedBooking.status === BOOKING_STATUS.NOT_SUBMITTED && (
              <p style={{ 
                fontSize: '12px', 
                color: '#0066cc', 
                fontStyle: 'italic',
                marginTop: '5px'
              }}>
                ℹ️ This booking has been selected but not yet submitted by the traveler.
              </p>
            )}
            {selectedBooking.status === BOOKING_STATUS.CONFIRMED && (
              <p style={{ 
                fontSize: '12px', 
                color: '#28a745', 
                fontStyle: 'italic',
                marginTop: '5px'
              }}>
                ✓ This booking is confirmed. Change to "Needs Attention" only if issues arise.
              </p>
            )}
            {selectedBooking.status === BOOKING_STATUS.NEEDS_ATTENTION && (
              <p style={{ 
                fontSize: '12px', 
                color: '#dc3545', 
                fontStyle: 'italic',
                marginTop: '5px'
              }}>
                ⚠️ This booking needs attention. You can mark it as Confirmed once resolved, or return to Pending for further review.
              </p>
            )}
          </div>
          
          {canChangeStatus ? (
            <>
              <div className="form-group">
                <label>Update Status</label>
                <select 
                  value={newStatus} 
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="form-control"
                  style={{
                    backgroundColor: STATUS_CONFIG[newStatus]?.bgColor,
                    color: STATUS_CONFIG[newStatus]?.textColor
                  }}
                >
                  {allowedStatuses.map(value => (
                    <option key={value} value={value}>
                      {STATUS_CONFIG[value]?.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Show instructions field when confirming booking */}
              {newStatus === BOOKING_STATUS.CONFIRMED && (
                <div className="form-group">
                  <label>Booking Instructions (sent to traveler)</label>
                  <textarea
                    value={bookingInstructions}
                    onChange={(e) => setBookingInstructions(e.target.value)}
                    className="form-control"
                    rows="4"
                    placeholder="Enter specific instructions for the traveler (e.g., booking reference, check-in details, special requirements)"
                    required
                  />
                  <small className="text-muted">These instructions will be sent to the traveler via email</small>
                </div>
              )}
              
              {/* Show reason field when marking needs attention */}
              {newStatus === BOOKING_STATUS.NEEDS_ATTENTION && (
                <div className="form-group">
                  <label>Reason for Attention Needed (sent to traveler)</label>
                  <textarea
                    value={needsAttentionReason}
                    onChange={(e) => setNeedsAttentionReason(e.target.value)}
                    className="form-control"
                    rows="4"
                    placeholder="Explain what needs attention or what action is required from the traveler"
                    required
                  />
                  <small className="text-muted">This explanation will be sent to the traveler</small>
                </div>
              )}
            </>
          ) : (
            <div className="alert alert-warning">
              Status transitions allowed:
              <ul style={{ marginTop: '10px', marginBottom: 0 }}>
                <li>Not Submitted → Pending (acknowledge receipt)</li>
                <li>Pending → Confirmed or Needs Attention</li>
                <li>Confirmed → Needs Attention (if issues arise)</li>
                <li>Needs Attention → Confirmed (when resolved) or Pending (for review)</li>
              </ul>
            </div>
          )}
          
          <div className="form-group">
            <label>Actual Price Paid (internal only)</label>
            <input
              type="text"
              value={actualPrice}
              onChange={(e) => setActualPrice(e.target.value)}
              className="form-control"
              placeholder="Enter actual amount paid (e.g., 25.50)"
              style={{ color: 'black' }}
            />
            <small className="text-muted">
              Estimated price: {selectedBooking.currency} {estimatedPrice} - Not visible to travelers
            </small>
          </div>
          
          {delta !== null && (
            <div className="form-group">
              <label>Price Delta (internal only)</label>
              <div className="form-control" style={{ 
                backgroundColor: '#f8f9fa', 
                color: delta > 0 ? '#dc3545' : delta < 0 ? '#28a745' : '#6c757d',
                fontWeight: 'bold'
              }}>
                {delta > 0 ? '+' : ''}{selectedBooking.currency} {delta}
                {delta > 0 && <span style={{ marginLeft: '10px', fontSize: '12px' }}>(Paid more than estimated)</span>}
                {delta < 0 && <span style={{ marginLeft: '10px', fontSize: '12px' }}>(Saved from estimate)</span>}
                {delta === 0 && <span style={{ marginLeft: '10px', fontSize: '12px' }}>(Exact match)</span>}
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label>Backend Notes (internal only)</label>
            <textarea
              value={backendNotes}
              onChange={(e) => setBackendNotes(e.target.value)}
              className="form-control"
              rows="3"
              placeholder="Internal notes about this booking (not sent to traveler)"
            />
            <small className="text-muted">These notes are for internal reference only</small>
          </div>
          
          <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => {
                deleteBooking(selectedBooking);
                setShowStatusModal(false);
                setSelectedBooking(null);
              }}
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              title="Delete this booking and allow traveler to reselect"
            >
              Delete Booking
            </button>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  // Validate required fields
                  if (newStatus === BOOKING_STATUS.CONFIRMED && !bookingInstructions.trim()) {
                    alert('Please provide booking instructions for the traveler');
                    return;
                  }
                  if (newStatus === BOOKING_STATUS.NEEDS_ATTENTION && !needsAttentionReason.trim()) {
                    alert('Please provide a reason for why attention is needed');
                    return;
                  }
                  
                  updateBookingStatus(selectedBooking, newStatus, {
                    actualPrice: actualPrice ? parseFloat(actualPrice) : null,
                    bookingInstructions: newStatus === BOOKING_STATUS.CONFIRMED ? bookingInstructions : selectedBooking.bookingInstructions,
                    needsAttentionReason: newStatus === BOOKING_STATUS.NEEDS_ATTENTION ? needsAttentionReason : selectedBooking.needsAttentionReason,
                    backendNotes,
                    priceDelta: delta ? parseFloat(delta) : null
                  });
                }}
                className="btn btn-primary"
                disabled={!canChangeStatus && newStatus !== selectedBooking.status}
              >
                Update Booking
              </button>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedBooking(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="admin-loading">Loading bookings...</div>;
  }

  return (
    <div className="admin-bookings-page">
      <h2>Booking Management</h2>
      
      {/* Summary Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <h4>Active Bookings</h4>
          <p>{activeBookings.length}</p>
        </div>
        <div className="stat-card">
          <h4>Pending</h4>
          <p>{activeBookings.filter(b => b.status === BOOKING_STATUS.PENDING).length}</p>
        </div>
        <div className="stat-card">
          <h4>Needs Attention</h4>
          <p>{activeBookings.filter(b => b.status === BOOKING_STATUS.NEEDS_ATTENTION).length}</p>
        </div>
        <div className="stat-card">
          <h4>Confirmed</h4>
          <p>{activeBookings.filter(b => b.status === BOOKING_STATUS.CONFIRMED).length}</p>
        </div>
        <div className="stat-card">
          <h4>Past Bookings</h4>
          <p>{pastBookings.length}</p>
        </div>
      </div>
      
      {/* Recent Audit Logs */}
      <div className="audit-section">
        <h3>Recent Status Changes</h3>
        <div className="audit-logs">
          {auditLogs.slice(0, 5).map(log => (
            <div key={log.id} className="audit-log-item">
              <span className="audit-timestamp">
                {log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString() : 'N/A'}
              </span>
              <span className="audit-action">
                {log.changedBy} changed {log.bookingDetails?.hostelName} from{' '}
                <span style={{ color: STATUS_CONFIG[log.previousStatus]?.color }}>
                  {STATUS_CONFIG[log.previousStatus]?.label}
                </span>{' '}
                to{' '}
                <span style={{ color: STATUS_CONFIG[log.newStatus]?.color }}>
                  {STATUS_CONFIG[log.newStatus]?.label}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>User</label>
            <select 
              value={filterByUser} 
              onChange={(e) => setFilterByUser(e.target.value)}
              className="form-control"
            >
              <option value="">All Users</option>
              {Object.entries(users).map(([userId, userData]) => (
                <option key={userId} value={userId}>
                  {userData.firstName} {userData.lastName} ({userData.email})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Journey</label>
            <select 
              value={filterByJourney} 
              onChange={(e) => setFilterByJourney(e.target.value)}
              className="form-control"
            >
              <option value="">All Journeys</option>
              {Object.entries(journeys).map(([journeyId, journeyData]) => (
                <option key={journeyId} value={journeyId}>
                  {journeyData.name || journeyData.routeName} - {journeyData.code}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Status</label>
            <select 
              value={filterByStatus} 
              onChange={(e) => setFilterByStatus(e.target.value)}
              className="form-control"
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_CONFIG)
                .filter(([value]) => value !== BOOKING_STATUS.PAST)
                .map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Date Range</label>
            <div className="date-range">
              <input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                className="form-control"
              />
              <span>to</span>
              <input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                className="form-control"
              />
            </div>
          </div>
          
          <button
            onClick={() => {
              setFilterByUser('');
              setFilterByJourney('');
              setFilterByStatus('');
              setDateFilter({ start: '', end: '' });
            }}
            className="btn btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Active Bookings Table */}
      <h3>Active Bookings</h3>
      <div className="table-container compact-table">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('bookingDate')} className="sortable">
                Booking Date {sortBy === 'bookingDate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('hostelName')} className="sortable">
                Hostel {sortBy === 'hostelName' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>User</th>
              <th>Journey</th>
              <th onClick={() => handleSort('price')} className="sortable">
                Est. Price {sortBy === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actual Price</th>
              <th onClick={() => handleSort('submittedAt')} className="sortable">
                Submitted {sortBy === 'submittedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedBookings.map((booking, index) => {
              const user = users[booking.userId] || {};
              const statusConfig = STATUS_CONFIG[booking.status] || STATUS_CONFIG[BOOKING_STATUS.AVAILABLE];
              
              return (
                <tr key={`active_${booking.id}_${index}`}>
                  <td>
                    <button
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowStatusModal(true);
                      }}
                      className="btn btn-sm btn-primary"
                      title="Update booking status and details"
                    >
                      Update
                    </button>
                  </td>
                  <td>
                    <div
                      className="status-indicator compact"
                      style={{
                        backgroundColor: statusConfig.bgColor,
                        color: statusConfig.textColor,
                        border: `1px solid ${statusConfig.color}`
                      }}
                      title={`Current status: ${statusConfig.label}`}
                    >
                      {statusConfig.label}
                    </div>
                  </td>
                  <td>{booking.bookingDate || 'N/A'}</td>
                  <td>
                    <strong>{booking.hostelName}</strong>
                    {booking.cityName && <div className="text-muted">{booking.cityName}</div>}
                  </td>
                  <td>
                    {user.firstName} {user.lastName}
                    <div className="text-muted">{user.email}</div>
                  </td>
                  <td>
                    {booking.journeyName}
                    <div className="text-muted">Day {booking.dayIndex + 1}</div>
                  </td>
                  <td>
                    {booking.currency} {booking.price || booking.pricePerBed || 0}
                  </td>
                  <td>
                    {booking.actualPrice ? 
                      `${booking.currency} ${booking.actualPrice}` : 
                      '-'
                    }
                  </td>
                  <td>
                    {booking.submittedAt ? 
                      new Date(booking.submittedAt.seconds ? 
                        booking.submittedAt.seconds * 1000 : 
                        booking.submittedAt
                      ).toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                  <td>
                    {booking.backendNotes ? 
                      <span title={booking.backendNotes} style={{ cursor: 'help' }}>
                        {booking.backendNotes.substring(0, 30)}{booking.backendNotes.length > 30 ? '...' : ''}
                      </span> : 
                      '-'
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredAndSortedBookings.length === 0 && (
          <div className="no-data">No bookings found matching your filters</div>
        )}
      </div>
      
      {/* Past Bookings Section */}
      <h3 style={{ marginTop: '40px' }}>Past Bookings</h3>
      
      {/* Past Bookings Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>User</label>
            <select 
              value={pastFilterByUser} 
              onChange={(e) => setPastFilterByUser(e.target.value)}
              className="form-control"
            >
              <option value="">All Users</option>
              {Object.entries(users).map(([userId, userData]) => (
                <option key={userId} value={userId}>
                  {userData.firstName} {userData.lastName} ({userData.email})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Journey</label>
            <select 
              value={pastFilterByJourney} 
              onChange={(e) => setPastFilterByJourney(e.target.value)}
              className="form-control"
            >
              <option value="">All Journeys</option>
              {Object.entries(journeys).map(([journeyId, journeyData]) => (
                <option key={journeyId} value={journeyId}>
                  {journeyData.name || journeyData.routeName} - {journeyData.code}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Date Range</label>
            <div className="date-range">
              <input
                type="date"
                value={pastDateFilter.start}
                onChange={(e) => setPastDateFilter({ ...pastDateFilter, start: e.target.value })}
                className="form-control"
              />
              <span>to</span>
              <input
                type="date"
                value={pastDateFilter.end}
                onChange={(e) => setPastDateFilter({ ...pastDateFilter, end: e.target.value })}
                className="form-control"
              />
            </div>
          </div>
          
          <button
            onClick={() => {
              setPastFilterByUser('');
              setPastFilterByJourney('');
              setPastDateFilter({ start: '', end: '' });
            }}
            className="btn btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Past Bookings Table */}
      <div className="table-container compact-table">
        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handlePastSort('bookingDate')} className="sortable">
                Booking Date {pastSortBy === 'bookingDate' && (pastSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handlePastSort('hostelName')} className="sortable">
                Hostel {pastSortBy === 'hostelName' && (pastSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>User</th>
              <th>Journey</th>
              <th onClick={() => handlePastSort('price')} className="sortable">
                Price {pastSortBy === 'price' && (pastSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handlePastSort('submittedAt')} className="sortable">
                Submitted {pastSortBy === 'submittedAt' && (pastSortOrder === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedPastBookings.map((booking, index) => {
              const user = users[booking.userId] || {};
              
              return (
                <tr key={`past_${booking.id}_${index}`}>
                  <td>{booking.bookingDate || 'N/A'}</td>
                  <td>
                    <strong>{booking.hostelName}</strong>
                    {booking.cityName && <div className="text-muted">{booking.cityName}</div>}
                  </td>
                  <td>
                    {user.firstName} {user.lastName}
                    <div className="text-muted">{user.email}</div>
                  </td>
                  <td>
                    {booking.journeyName}
                    <div className="text-muted">Day {booking.dayIndex + 1}</div>
                  </td>
                  <td>
                    {booking.currency} {booking.price || booking.pricePerBed || 0}
                  </td>
                  <td>
                    {booking.actualPrice ? 
                      `${booking.currency} ${booking.actualPrice}` : 
                      '-'
                    }
                  </td>
                  <td>
                    {booking.submittedAt ? 
                      new Date(booking.submittedAt.seconds ? 
                        booking.submittedAt.seconds * 1000 : 
                        booking.submittedAt
                      ).toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredAndSortedPastBookings.length === 0 && (
          <div className="no-data">No past bookings found matching your filters</div>
        )}
      </div>
      
      {/* Status Change Modal */}
      {showStatusModal && <StatusChangeModal />}
    </div>
  );
}